import { useEffect, useState } from "react";
import api from "@/utils/axios";
import cls from "../financePage.module.scss";

const DEFAULT_LIMIT = 50;

const buildDateValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const ExchangeRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    baseCurrency: "CAD",
    rate: "",
    effectiveFrom: "",
  });
  const [editingId, setEditingId] = useState(null);

  const fetchRates = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/finance/exchange-rates", {
        params: { limit: DEFAULT_LIMIT },
      });
      setRates(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load exchange rates",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const resetForm = () => {
    setForm({ baseCurrency: "CAD", rate: "", effectiveFrom: "" });
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setError("");
      const payload = {
        baseCurrency: form.baseCurrency,
        rate: Number(form.rate),
        effectiveFrom: form.effectiveFrom || undefined,
      };

      if (editingId) {
        await api.patch(`/v1/admin/finance/exchange-rates/${editingId}`, payload);
      } else {
        await api.post("/v1/admin/finance/exchange-rates", payload);
      }

      await fetchRates();
      resetForm();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save exchange rate",
      );
    }
  };

  const startEdit = (rate) => {
    setEditingId(rate._id);
    setForm({
      baseCurrency: rate.baseCurrency || "CAD",
      rate: rate.rate ?? "",
      effectiveFrom: buildDateValue(rate.effectiveFrom),
    });
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <p className={cls.eyebrow}>Finance • Exchange Rates</p>
          <h1 className={cls.title}>Exchange rates</h1>
          <p className={cls.subtitle}>
            Create and update base currency rates for wallet conversions.
          </p>
        </div>
      </header>

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>
          {editingId ? "Update exchange rate" : "Create new rate"}
        </h2>
        <form className={cls.filters} onSubmit={handleSubmit}>
          <label className={cls.formRow}>
            <span className={cls.label}>Base currency</span>
            <input
              className={cls.input}
              value={form.baseCurrency}
              onChange={handleChange("baseCurrency")}
              placeholder="CAD"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Rate</span>
            <input
              className={cls.input}
              type="number"
              min="0"
              step="0.0001"
              value={form.rate}
              onChange={handleChange("rate")}
              placeholder="1.0000"
            />
          </label>
          <label className={cls.formRow}>
            <span className={cls.label}>Effective from</span>
            <input
              className={cls.input}
              type="date"
              value={form.effectiveFrom}
              onChange={handleChange("effectiveFrom")}
            />
          </label>
          <div className={cls.inlineActions}>
            <button className={`${cls.btn} ${cls.btnPrimary}`} type="submit">
              {editingId ? "Save changes" : "Create rate"}
            </button>
            {editingId && (
              <button
                type="button"
                className={`${cls.btn} ${cls.btnSecondary}`}
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      {error && (
        <div className={`${cls.stateBox} ${cls.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={cls.card}>
        <h2 className={cls.sectionTitle}>Latest rates</h2>
        {loading ? (
          <div className={cls.stateBox}>Loading exchange rates…</div>
        ) : rates.length === 0 ? (
          <div className={cls.stateBox}>No exchange rates yet.</div>
        ) : (
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>Base</th>
                  <th>Rate</th>
                  <th>Effective</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr key={rate._id}>
                    <td>{rate.baseCurrency}</td>
                    <td>{rate.rate}</td>
                    <td>{buildDateValue(rate.effectiveFrom) || "-"}</td>
                    <td>{buildDateValue(rate.createdAt) || "-"}</td>
                    <td>
                      <div className={cls.inlineActions}>
                        <button
                          type="button"
                          className={cls.btn}
                          onClick={() => startEdit(rate)}
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ExchangeRates;